import { type FC, useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import { Checkbox, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { SearchIcon } from "@snapchat/snap-design-system-icons";
import { type ColumnDef, type SortingState } from "@tanstack/react-table";
import { useIntl } from "react-intl";

import { AbsoluteRoute } from "../../../../core/routes/absoluteRoutes";
import { RouteEntities } from "../../../../core/routes/relativeRoutes";
import { type RewardedLens } from "../../../../gql-generated/types";
import { VirtualTable } from "../../../../shared/components/virtual-table";
import { useLensIdSearchParameter } from "../../../hooks";
import { NoRewardedLenses, RewardedLensesTitle, SelectLensSubtitle, messages } from "../messages.generated";

import { COLUMN_NAME, COLUMN_SELECT, rewardedLensesColumns } from "./RewardedLensesColumns";
import { container, emptyMessage, search, selectCheckbox } from "./RewardedLensesTable.module.css";

const FLEX_COLUMNS = [COLUMN_NAME];
const SKELETON_ROW_COUNT = 5;
const SKELETON_ROW_HEIGHT = 36;
const SKELETON_HEADER_HEIGHT = 22;
const SKELETON_SEARCH_HEIGHT = 36;

interface RewardedLensesTableProps {
    lenses: RewardedLens[];
    loading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
}

export const RewardedLensesTable: FC<RewardedLensesTableProps> = ({ lenses, loading, hasMore, onLoadMore }) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [sorting, setSorting] = useState<SortingState>([]);
    const { lensId: selectedLensId, setLensId, clearLensId } = useLensIdSearchParameter();

    const selectColumn: ColumnDef<RewardedLens> = useMemo(
        () => ({
            id: COLUMN_SELECT,
            header: () => null,
            cell: ({ row }) => {
                const { lens } = row.original;
                const lensId = lens?.id;
                const isSelected = selectedLensId === lensId;
                return (
                    <div
                        className={selectCheckbox}
                        role="presentation"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Checkbox
                            checked={isSelected}
                            onChange={() => {
                                if (isSelected) {
                                    clearLensId();
                                } else if (lensId) {
                                    const lensOrgId = lens.organizationId;
                                    const lensFolderId = lens.lensAccountId?.lensSpaceId;
                                    const path = lensFolderId
                                        ? generatePath(AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS_TAB, {
                                              orgId: lensOrgId,
                                              lensFolderId,
                                              insightsTab: RouteEntities.Monetization,
                                          })
                                        : undefined;
                                    if (path) {
                                        setLensId(lensId, path);
                                    }
                                }
                            }}
                        />
                    </div>
                );
            },
            size: 40,
            enableSorting: false,
        }),
        [clearLensId, selectedLensId, setLensId],
    );

    const columns = useMemo(() => [selectColumn, ...rewardedLensesColumns], [selectColumn]);

    const handleRowClick = useCallback(
        (row: RewardedLens) => {
            const lensId = row.lens?.id;
            if (!lensId) return;
            return () => {
                navigate(generatePath(AbsoluteRoute.LENS, { lensId }));
            };
        },
        [navigate],
    );

    if (loading) {
        return (
            <div className={container}>
                <Stack gap="md">
                    <Skeleton h={SKELETON_HEADER_HEIGHT} w="30%" />
                    <Skeleton h={SKELETON_SEARCH_HEIGHT} w="100%" />
                    {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                        <Skeleton key={i} h={SKELETON_ROW_HEIGHT} w="100%" />
                    ))}
                </Stack>
            </div>
        );
    }

    const header = (
        <TextInput
            className={search}
            leftSection={<SearchIcon />}
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            value={searchTerm}
            onChange={(e) => {
                setSearchTerm(e.currentTarget.value);
            }}
        />
    );

    return (
        <div className={container}>
            <VirtualTable<RewardedLens>
                data={lenses}
                columns={columns}
                title={<RewardedLensesTitle />}
                subtitle={<SelectLensSubtitle />}
                globalFilter={searchTerm}
                onGlobalFilterChange={setSearchTerm}
                sorting={sorting}
                onSortingChange={setSorting}
                flexColumns={FLEX_COLUMNS}
                onRowClick={handleRowClick}
                hasMore={hasMore}
                onLoadMore={onLoadMore}
                header={header}
                emptyMessage={
                    <div className={emptyMessage}>
                        <Text className="paragraph-p200" c="dimmed">
                            <NoRewardedLenses />
                        </Text>
                    </div>
                }
            />
        </div>
    );
};
